import { DrawerMenuItemList } from 'Components/Layout/DrawerMenu/DrawerMenuItemList'
import type { NavLinkItem } from 'Components/Layout/DrawerMenu/types'
import { WithLoader } from 'Components/WithLoader'
import { useGetCatalogsQuery } from 'Services/ProductManagement/Features/Catalog/catalogApi'
import type { FetchCatalogListParams } from 'Services/ProductManagement/Features/Catalog/types'
import { TariffContext } from 'Services/ProductManagement/Features/Tariff/Context/tariffContext'
import { useAllTariffs } from 'Services/ProductManagement/Features/Tariff/Hooks'
import type { Tariff } from 'Services/ProductManagement/Features/Tariff/types'
import {
  IS_HSL_SERVICE_ENVIRONMENT,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PRODUCT_MANAGEMENT_ROOT_URL
} from 'Util/constants'
import AddIcon from '@mui/icons-material/Add'
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
  styled,
  Typography
} from '@mui/material'
import { useCallback, useContext, useMemo } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { t } from 'ttag'

/**
 * TariffDrawerMenuContent component for rendering tariff navigation menu
 *
 * Automatically adapts behavior based on IS_HSL_SERVICE_ENVIRONMENT:
 * - HSL environment: Simplified navigation without catalog sub-menu
 * - Standard environment: Full navigation with catalog sub-menu (default)
 */

export const StyledMenuItem = styled(MenuItem)(() => ({
  padding: 0
}))

export const StyledNavLink = styled(NavLink)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.text.primary,
  width: '100%',
  padding: theme.spacing(1, 2)
}))

function TariffDrawerMenuContent() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const navigate = useNavigate()

  const { allTariffs, isAllTariffsLoading } = useAllTariffs()

  const { selectedTariffId, setSelectedTariffId } = useContext(TariffContext)

  // Environment-based feature flag
  const disableCatalogSubMenu = IS_HSL_SERVICE_ENVIRONMENT

  // Memoize the catalog fetch parameters to prevent unnecessary API calls
  const catalogFetchParams: FetchCatalogListParams = useMemo(
    () => ({
      tariff_id: selectedTariffId,
      bypass_pagination: true
    }),
    [selectedTariffId]
  )

  // Don't fetch catalogs if catalog sub-menu is disabled
  const catalogQuery = useGetCatalogsQuery(catalogFetchParams, {
    refetchOnMountOrArgChange: true,
    skip: disableCatalogSubMenu || !selectedTariffId
  })

  const { data: { resources: catalogs } = { resources: [] }, isFetching: isCatalogsFetching } =
    catalogQuery

  // Memoize the navigation links to prevent recreation on every render
  const drawerMenuNavLinks: NavLinkItem[] = useMemo(() => {
    const baseNavLinks: NavLinkItem[] = [
      {
        title: t`Tariff products`,
        link: `${baseUrl}/${selectedTariffId}/products`
      },
      {
        title: t`Tariff details`,
        link: `${baseUrl}/${selectedTariffId}/details`
      }
    ]

    const salesChannelLink: NavLinkItem = disableCatalogSubMenu
      ? {
          title: t`Tariff sales channels`,
          link: `${baseUrl}/${selectedTariffId}/sales-channels`
        }
      : {
          title: t`Tariff sales channels`,
          link: `${baseUrl}/${selectedTariffId}/sales-channels`,
          isSubMenuLoading: isCatalogsFetching,
          subMenuItems: catalogs.map(catalog => ({
            title: catalog.name,
            link: `${baseUrl}/${selectedTariffId}/sales-channels/${catalog.id}/products/`,
            suffix: catalog.product_ids.length.toString()
          }))
        }

    return [...baseNavLinks, salesChannelLink]
  }, [baseUrl, selectedTariffId, disableCatalogSubMenu, isCatalogsFetching, catalogs])

  // Memoize the select change handler to prevent recreation
  const handleTariffSelectChange = useCallback(
    (event: SelectChangeEvent) => {
      const { value } = event.target
      setSelectedTariffId(value)
    },
    [setSelectedTariffId]
  )

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Typography variant="h3" noWrap component="div" sx={{ fontFamily: 'Prompt', mt: 1, mb: 3 }}>
          {t`Tariffs`}
        </Typography>
        <WithLoader loading={isAllTariffsLoading} rows={1}>
          <FormControl sx={{ mb: 2, width: '100%' }}>
            <InputLabel shrink>{t`Tariff`}</InputLabel>
            <Select
              sx={{ backgroundColor: 'background.default', a: { padding: 0 } }}
              value={selectedTariffId}
              label={t`Tariffs`}
              displayEmpty
              onChange={handleTariffSelectChange}
              input={<OutlinedInput notched label={t`Tariffs`} />}
            >
              <StyledMenuItem value="">
                <StyledNavLink to={baseUrl}>{t`All tariffs`}</StyledNavLink>
              </StyledMenuItem>
              {allTariffs.map((tariff: Tariff) => (
                <StyledMenuItem key={tariff.id} value={tariff.id}>
                  <StyledNavLink to={`${baseUrl}/${tariff.id}/products`}>
                    {tariff.description}
                  </StyledNavLink>
                </StyledMenuItem>
              ))}
            </Select>
          </FormControl>
        </WithLoader>
      </Box>
      <Divider />
      {selectedTariffId && (
        <DrawerMenuItemList navLinks={drawerMenuNavLinks} rootUrl={baseUrl}>
          {IS_WALTTI_SERVICE_ENVIRONMENT && (
            <Button
              sx={{ m: 1 }}
              variant="text"
              size="small"
              onClick={() => navigate(`${baseUrl}/${selectedTariffId}/sales-channels/create/`)}
              startIcon={<AddIcon />}
            >
              {t`Create new sales channel`}
            </Button>
          )}
        </DrawerMenuItemList>
      )}
    </>
  )
}

export default TariffDrawerMenuContent
